import { getToken, clearToken } from '../utils/authToken';
/**
 * EmailVerificationBanner - Banner para usuarios no verificados
 * Muestra un aviso y permite reenviar el email de verificación
 */
import { useState } from 'react';
import { Mail, Send, Loader2, X, CheckCircle, AlertTriangle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';
import axios from 'axios';

import { API_URL } from '../config';

const EmailVerificationBanner = ({ onVerified }) => {
  const { language } = useLanguage();
  const isSpanish = language === 'es';
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  
  const handleResend = async () => {
    setSending(true);
    try {
      const token = getToken();
      const response = await axios.post(
        `${API_URL}/api/auth/resend-verification`,
        {},
        {}
      );
      
      if (response.data.already_verified) {
        toast.success(isSpanish ? '¡Tu email ya está verificado!' : 'Your email is already verified!');
        if (onVerified) onVerified();
        return;
      }
      
      setSent(true);
      toast.success(isSpanish ? '¡Email enviado! Revisa tu bandeja de entrada' : 'Email sent! Check your inbox');
    } catch (error) {
      toast.error(isSpanish ? 'Error al enviar email' : 'Error sending email');
    } finally {
      setSending(false);
    }
  };
  
  if (dismissed) return null;
  
  return (
    <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg px-4 py-3 mb-4">
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center">
          <AlertTriangle className="w-4 h-4 text-amber-400" />
        </div>
        
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-amber-300">
            {isSpanish ? 'Tu email no está verificado' : 'Your email is not verified'}
          </h4>
          <p className="text-xs text-amber-400/80 mt-0.5">
            {isSpanish 
              ? 'Revisa tu correo y haz clic en el enlace de verificación para comentar y publicar apps.' 
              : 'Check your email and click the verification link to comment and publish apps.'
            }
          </p>
          
          <div className="flex items-center gap-2 mt-2">
            {sent ? (
              <span className="text-xs text-green-400 flex items-center gap-1">
                <CheckCircle className="w-3 h-3" />
                {isSpanish ? 'Email enviado' : 'Email sent'}
              </span>
            ) : (
              <button
                onClick={handleResend}
                disabled={sending}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 text-xs rounded-lg transition-colors disabled:opacity-50"
              >
                {sending ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <Send className="w-3 h-3" />
                )}
                {isSpanish ? 'Reenviar email' : 'Resend email'}
              </button>
            )}
          </div>
        </div>
        
        <button
          onClick={() => setDismissed(true)}
          className="flex-shrink-0 p-1 hover:bg-amber-500/20 rounded transition-colors"
        >
          <X className="w-4 h-4 text-amber-400/60" />
        </button>
      </div>
    </div>
  );
};

export default EmailVerificationBanner;
